import { dayMonth } from '../lib/api';

export type TrendSeries = {
  label: string;
  points: Array<{ date: string; value: number }>;
  /** Referenz-Kollege: gestrichelt, als Vergleichsmassstab. */
  dashed?: boolean;
};

/**
 * Kleiner Verlaufs-Chart als reines SVG – bewusst ohne Chart-Bibliothek. Für
 * zwei Linien über acht Wochen wären ein paar hundert Kilobyte JavaScript nicht
 * zu rechtfertigen, und die CSP erlaubt ohnehin keine externen Skripte.
 */
export function Trend({ series, unitLabel }: { series: TrendSeries[]; unitLabel: string }) {
  const all = series.flatMap((s) => s.points);
  if (all.length < 2) {
    return <p className="card__meta">Noch zu wenige Snapshots für einen Verlauf – kommt mit den nächsten Tagen.</p>;
  }

  const dates = [...new Set(all.map((p) => p.date))].sort();
  const max = Math.max(1, ...all.map((p) => p.value));
  const w = 100;
  const h = 40;
  const x = (date: string) => (dates.indexOf(date) / Math.max(1, dates.length - 1)) * w;
  const y = (value: number) => h - (value / max) * (h - 4) - 2;

  return (
    <>
      <svg className="sm-trend" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" role="img"
           aria-label={`Verlauf ${unitLabel}`}>
        {series.map((s, i) => {
          const path = s.points
            .slice()
            .sort((a, b) => a.date.localeCompare(b.date))
            .map((p, idx) => `${idx === 0 ? 'M' : 'L'}${x(p.date).toFixed(2)},${y(p.value).toFixed(2)}`)
            .join(' ');
          return (
            <path
              key={s.label}
              d={path}
              fill="none"
              stroke={i === 0 && !s.dashed ? 'var(--sdc-teal)' : 'var(--sdc-muted)'}
              strokeWidth={s.dashed ? 0.6 : 1}
              strokeDasharray={s.dashed ? '2 2' : undefined}
              vectorEffect="non-scaling-stroke"
            />
          );
        })}
      </svg>
      <div className="sm-legend">
        <span>
          {unitLabel} · {dayMonth(dates[0]!)} bis {dayMonth(dates.at(-1)!)} · Maximum {max}
        </span>
        {series.map((s) => (
          <span key={s.label}>
            {s.dashed ? '– – ' : '—— '}
            {s.label}
          </span>
        ))}
      </div>
    </>
  );
}
